import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { CustomerRatingsFragmentFragmentDoc } from '@packages/gql/generated/shopping/CustomerRatingsFragmentFragmentDoc';
import { RatingSeoResultFragmentFragmentDoc } from '@packages/gql/generated/shopping/RatingSeoResultFragmentFragmentDoc';
import { LazyRender } from '@packages/modules';
import {
  Card,
  CardContent,
  CardHeader,
  Grid,
  RatingIcons,
  Stack,
  Typography,
} from '@packages/shared';
import { useIntl } from 'react-intl';
import { AddRating } from './AddRating';
import { CustomerRatingsFallback } from './CustomerRatingsFallback';
import { RatingInfo } from './RatingInfo';
import { RatingTags } from './RatingTags';
import { Review } from './Review';
import { ReviewStatistic } from './ReviewStatistic';
import { customerRatingsMessages } from './messages';
import { CustomerRatingsShowAll } from './CustomerRatingsShowAll';
import { SlotBoxDV } from '../SlotBoxDV';
import { useWriteCustomerRatingEnabled } from './useWriteCustomerRatingEnabled';

/* GraphQL */ `
  fragment RatingSeoResultFragment on RatingResult {
    product {
      avgRating
      reviewCount
      reviews {
        ...ReviewFragment
      }
      tags {
        ...RatingTagsFragment
      }
      reviewStatistics {
        ...ReviewStatisticFragment
      }
      sku
    }
    verifiedRatingsHintLayerId
  }
`;

/* GraphQL */ `
  fragment CustomerRatingsFragment on Product {
    ...CustomerRatingsShowAllFragment
    ...AddRatingFragment
    ...RatingTagSkuFragment
  }
`;

type CustomerRatingsProps = {
  /**
   * RatingResult product data
   */
  ratingResultData: FragmentType<typeof RatingSeoResultFragmentFragmentDoc>;
  /**
   * ProductData for glycerin submitRating event
   */
  productData: FragmentType<typeof CustomerRatingsFragmentFragmentDoc>;
};

/**
 * Customer ratings slot component displayed in the detailview, containing statistics, tag lists for filtering, max 3 reviews and the add rating button
 */
export const CustomerRatings = ({ ratingResultData, productData }: CustomerRatingsProps) => {
  const { product: ratingResultproduct, verifiedRatingsHintLayerId } = getFragmentData(
    RatingSeoResultFragmentFragmentDoc,
    ratingResultData,
  );
  const product = getFragmentData(CustomerRatingsFragmentFragmentDoc, productData);

  const { formatMessage } = useIntl();
  const writeCustomerRatingEnabled = useWriteCustomerRatingEnabled();

  if (!ratingResultproduct) {
    return null;
  }

  const { avgRating, reviewCount, reviews, tags, reviewStatistics } = ratingResultproduct;

  /* 
    The whole DV customer rating section has to be deactivated if:
      - writeCustomerRatingEnabled is false
      - there are no customer ratings for a product available

    This would end up into an empty section which should be avoided.
  */
  if (!writeCustomerRatingEnabled && reviewCount === 0) return null;

  return (
    <>
      <SlotBoxDV mode="headline">
        <Typography
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            my: 2,
            typography: ['h4', 'h4', 'h3'],
          }}
          component="header"
        >
          {formatMessage(customerRatingsMessages.headline)}
          <RatingInfo verifiedRatingsHintLayerId={verifiedRatingsHintLayerId} />
        </Typography>
      </SlotBoxDV>
      <SlotBoxDV mode="slotbox">
        <LazyRender
          key={`${ratingResultproduct.sku}-customer-ratings`}
          fallback={
            <CustomerRatingsFallback
              // previewReviewsCount={reviews.length}
              previewReviewsCount={1}
              reviewsCount={reviewCount}
            />
          }
          options={{ threshold: 0.1 }}
        >
          <Grid
            container
            columnSpacing={{ xs: 2, sm: 2, md: 2, lg: 5.5 }}
            rowSpacing={1.5}
            id="customer-ratings"
          >
            <Grid
              item
              xs={12}
              md={6}
              lg={5}
              sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
            >
              {reviewCount > 0 && (
                <Card>
                  <CardHeader
                    sx={{ borderBottom: 1, borderColor: 'grey.main', py: 1, px: 2 }}
                    title={formatMessage(customerRatingsMessages.title)}
                  />
                  <CardContent>
                    <RatingIcons avgRating={avgRating} reviewCount={reviewCount} sx={{ mb: 0.5 }} />
                    <Stack spacing={0.5} mb={3}>
                      {reviewStatistics.map((rs, index) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <ReviewStatistic data={rs} key={index} />
                      ))}
                    </Stack>
                    <RatingTags ratingTagsData={tags} skuData={product} />
                  </CardContent>
                </Card>
              )}
              {writeCustomerRatingEnabled && <AddRating productData={product} />}
            </Grid>
            <Grid
              item
              xs={12}
              md={6}
              lg={7}
              sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
            >
              {reviewCount > 0 && (
                <>
                  {reviews.map((review, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <Review key={index} reviewData={review} />
                  ))}
                  <CustomerRatingsShowAll data={product} reviewCount={reviewCount} />
                </>
              )}
            </Grid>
          </Grid>
        </LazyRender>
      </SlotBoxDV>
    </>
  );
};
